import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [firm, setFirm] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setEmail(""); setFirm(""); setRole(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { email, firm_name: firm || null, role: role || null });
      toast.success("You're on the list. We'll be in touch.");
      reset();
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="waitlist-dialog"
        className="sm:max-w-lg rounded-none border border-border bg-white p-0"
      >
        <div className="p-8">
          <DialogHeader className="text-left space-y-2">
            <p className="overline">Limited early access</p>
            <DialogTitle className="font-serif text-3xl tracking-tight font-light">
              Join the ContractIQ waitlist
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Be among the first law firms to deploy contract intelligence built for partners, associates, and in-house counsel.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="overline block mb-2" htmlFor="wl-email">Work email *</label>
              <input
                id="wl-email"
                data-testid="waitlist-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@firm.com"
                className="w-full border border-border bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-foreground focus:border-foreground"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2" htmlFor="wl-firm">Firm</label>
                <input
                  id="wl-firm"
                  data-testid="waitlist-firm-input"
                  value={firm}
                  onChange={(e) => setFirm(e.target.value)}
                  placeholder="Your firm"
                  className="w-full border border-border bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-foreground focus:border-foreground"
                />
              </div>
              <div>
                <label className="overline block mb-2" htmlFor="wl-role">Role</label>
                <input
                  id="wl-role"
                  data-testid="waitlist-role-input"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="Partner, Counsel, Ops…"
                  className="w-full border border-border bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-foreground focus:border-foreground"
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="waitlist-submit-btn"
              disabled={loading}
              className="w-full mt-2 px-6 py-3.5 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#002147] transition-colors disabled:opacity-60"
            >
              {loading ? "Submitting…" : "Request early access"}
            </button>
            <p className="text-xs text-muted-foreground font-mono tracking-wide">
              By joining, you agree to be contacted about ContractIQ. We never share your email.
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
